import { useEffect, useState } from "react";
import { api, apiErr, API_BASE } from "../lib/api";
import { toast } from "sonner";
import { FileCode, AlertTriangle, CheckCircle2, Download } from "lucide-react";

// Downloads the Appendix D payroll extract (.BLK) for the given finalized ROEs, after a local pre-flight.
export default function ROEExtractPanel({ companyId, roeIds = null, canEdit, compact = false, onDone }) {
  const [pre, setPre] = useState(null);
  const [issue, setIssue] = useState("D");
  const [busy, setBusy] = useState(false);
  const idsQ = roeIds ? `?roe_ids=${roeIds.join(",")}` : "";

  useEffect(() => {
    if (!companyId) return;
    api.get(`/roe-workflow/${companyId}/extract/preflight${idsQ}`).then((r) => setPre(r.data)).catch(() => setPre(null));
  }, [companyId, idsQ]);

  if (!pre || pre.roes.length === 0) return null;
  const ready = pre.roes.filter((r) => r.ready);
  const blocked = pre.roes.filter((r) => !r.ready);

  const download = async () => {
    setBusy(true);
    try {
      const ids = ready.map((r) => r.roe_id).join(",");
      const res = await fetch(`${API_BASE}/roe-workflow/${companyId}/extract/file.blk?roe_ids=${ids}&issue=${issue}`, {
        credentials: "include", headers: localStorage.getItem("impersonation_token") ? { Authorization: `Bearer ${localStorage.getItem("impersonation_token")}` } : {},
      });
      if (!res.ok) { const j = await res.json().catch(() => ({})); throw { response: { data: j } }; }
      const blob = await res.blob();
      const name = (res.headers.get("content-disposition") || "").match(/filename="([^"]+)"/)?.[1] || "ROE_extract.BLK";
      const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = name; a.click(); URL.revokeObjectURL(a.href);
      toast.success(`Payroll extract downloaded (${ready.length} ROE${ready.length === 1 ? "" : "s"}) — upload it in ROE Web`);
      onDone && onDone();
    } catch (e) { toast.error(apiErr(e)); } finally { setBusy(false); }
  };

  return (
    <div data-testid="roe-extract-panel" className={`border border-border bg-white ${compact ? "p-4" : "p-5"} ${compact ? "" : "mt-6"}`}>
      <div className="flex flex-wrap items-center gap-3">
        <FileCode className="h-4 w-4" />
        <h2 className="font-bold tracking-tight">ROE Web Payroll Extract (.BLK)</h2>
        <span className="text-xs text-muted-foreground">Appendix D · XML version W-2.0 · {ready.length} ready{blocked.length ? ` · ${blocked.length} blocked` : ""}</span>
        <div className="ml-auto flex items-center gap-2">
          <select data-testid="roe-extract-issue" value={issue} onChange={(e) => setIssue(e.target.value)} className="border border-input bg-white px-2 py-1.5 text-xs">
            <option value="D">Load as Draft in ROE Web</option>
            <option value="S">Submit on upload</option>
          </select>
          {canEdit && <button data-testid="roe-extract-download" disabled={busy || ready.length === 0} onClick={download} className="inline-flex items-center gap-1.5 bg-black text-white px-4 py-2 text-sm hover:bg-neutral-800 disabled:opacity-40">
            <Download className="h-4 w-4" /> {busy ? "Building…" : "Create extract file"}
          </button>}
        </div>
      </div>
      {!pre.contact_configured && (
        <p data-testid="roe-extract-no-contact" className="mt-3 text-xs text-amber-700 flex items-center gap-1.5"><AlertTriangle className="h-3.5 w-3.5" /> Block 16 contact comes from Settings → CRA Transmitter (contact name, area code, phone). Set it before creating the file.</p>
      )}
      <ul className="mt-3 space-y-1 text-sm">
        {pre.roes.map((r) => (
          <li key={r.roe_id} data-testid={`roe-extract-row-${r.roe_id}`} className="flex flex-wrap items-start gap-2">
            {r.ready ? <CheckCircle2 className="h-4 w-4 text-emerald-600 mt-0.5" /> : <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5" />}
            <span className="font-medium">{r.employee_name}</span>
            <span className="text-xs text-muted-foreground">last day {r.last_day} · {r.status} · {r.filing_status}</span>
            {r.issues.length > 0 && <ul className="basis-full pl-6 text-xs">{r.issues.map((i, k) => <li key={k} className={i.severity === "BLOCKER" ? "text-red-700" : "text-amber-700"}>{i.message}</li>)}</ul>}
          </li>
        ))}
      </ul>
      <p className="mt-3 text-[11px] text-muted-foreground">Built only from the frozen finalized ROE. Downloading marks the ROE as Exported; confirm filing after ROE Web accepts it. Blocks 17A–C and 19 (vacation, holiday, other monies, special payments) are completed in ROE Web when applicable.</p>
    </div>
  );
}
